import { useEffect, useState } from "react";
import { Minus, Plus } from "lucide-react";

export const MAX_QTY = 999;
export const clampQty = (n, min = 1) =>
  Math.min(MAX_QTY, Math.max(min, Math.floor(Number(n)) || min));

const SIZES = {
  lg: { btn: "w-12 h-12", icon: "w-5 h-5", input: "w-20 h-12 text-3xl" },
  sm: { btn: "w-6 h-6", icon: "w-3 h-3", input: "w-12 h-7 text-sm" },
};

// -/+ buttons with a typeable number field. `min` applies to the minus button only.
export function QuantityInput({ value, onChange, min = 1, size = "sm", testIds = {} }) {
  const [draft, setDraft] = useState(String(value));
  const s = SIZES[size];

  useEffect(() => setDraft(String(value)), [value]);

  const handleType = (e) => {
    const digits = e.target.value.replace(/\D/g, "").slice(0, 3);
    setDraft(digits);
    const n = parseInt(digits, 10);
    if (n >= 1 && n !== value) onChange(n);
  };

  const step = (delta) => onChange(clampQty(value + delta, min));

  return (
    <div className="flex items-center gap-2">
      <button
        type="button"
        onClick={() => step(-1)}
        className={`${s.btn} rounded-full bg-gray-200 hover:bg-gray-300 flex items-center justify-center transition-colors`}
        data-testid={testIds.dec}
        aria-label="Decrease quantity"
      >
        <Minus className={s.icon} />
      </button>
      <input
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        value={draft}
        onChange={handleType}
        onFocus={(e) => e.target.select()}
        onBlur={() => setDraft(String(value))}
        onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), e.currentTarget.blur())}
        className={`${s.input} text-center font-bold rounded-lg border border-gray-200 bg-white focus:outline-none focus:ring-2 focus:ring-[#1aa59a]/40 focus:border-[#1aa59a]`}
        data-testid={testIds.input}
        aria-label="Quantity"
      />
      <button
        type="button"
        onClick={() => step(1)}
        disabled={value >= MAX_QTY}
        className={`${s.btn} rounded-full bg-[#1aa59a] hover:bg-[#158a80] disabled:opacity-40 text-white flex items-center justify-center transition-colors`}
        data-testid={testIds.inc}
        aria-label="Increase quantity"
      >
        <Plus className={s.icon} />
      </button>
    </div>
  );
}
